import { Card as CardType, Control } from '@yleisradio/areena-types';
import classNames from 'classnames';
import { Controls } from 'components/Controls';
import { GridContainer, GridElement } from 'components/Grid';
import { useAreenaService } from 'contexts/AreenaServiceContext';
import { useTranslation } from 'hooks/useTranslation';
import React, { useState } from 'react';
import { getCardKey, getMediaType, getRegion } from 'utils/card';
import { getFirstLineOfText, sanitizeHtmlId } from 'utils/ui-helpers';
import { LogoImage } from '../LogoImage';
import { GenericHeaderProps } from '../types';
import styles from './GenericHeader.module.scss';
import { HeaderImage } from './HeaderImage';
import { HeaderPlayControls } from './HeaderPlayControls';
import { HeaderLabels } from './HeaderLabels';
import { ShowMore } from './ShowMore';

function isTitleLarge(card: CardType | undefined): boolean {
  return (
    !!card && !card.overlayLogoImage && !card.controls?.some(isPreTitleControl)
  );
}

function isPreTitleControl(control: Control) {
  return ['back', 'series'].includes(control.tag ?? '');
}

export const GenericHeader: React.FunctionComponent<
  GenericHeaderProps & { viewUuid: string }
> = ({
  viewKey,
  hasBackgroundImage,
  headerCard,
  headerImageAspectRatio,
  viewLogoImage,
  viewTitle,
  viewUuid,
}) => {
  const {
    description,
    image,
    controls = [],
    labels = [],
    overlayLogoImage = viewLogoImage,
  } = headerCard;

  const t = useTranslation();
  const { areenaService } = useAreenaService();

  const [isShowingMore, setIsShowingMore] = useState(false);

  const [isDescriptionOverflowing, setIsDescriptionOverflowing] =
    useState(false);
  const firstLineOfDescription = getFirstLineOfText(description);
  const hasMoreDescription =
    isDescriptionOverflowing || description !== firstLineOfDescription;

  const cardKey = getCardKey(headerCard, 0, viewKey);
  const linksHeadingId = sanitizeHtmlId(`${cardKey}-links`);
  const programInfoHeadingId = sanitizeHtmlId(`${cardKey}-info`);

  const headerClassName = classNames(
    styles.header,
    hasBackgroundImage && styles.headerWithBackgroundImage,
    !!overlayLogoImage && styles.headerWithLogo,
    !!image && styles.headerWithImage
  );

  const preTitleControls = controls.filter(isPreTitleControl);

  const playControl = controls.find((c) => c.tag === 'play');
  const queueControl = controls.find((c) => c.tag === 'queue');

  const programMediaType = getMediaType(labels);
  const programRegion = getRegion(labels);

  const actionControls = controls.filter(
    (c) => !c.tag || c.tag === 'downloadLink'
  );
  const linkControls = controls.filter((c) => c.tag === 'link');

  const programInfoLabels = labels.filter(
    (l) => l.type === 'generic' && l.priority === 2
  );

  const descriptionElement = (
    <p
      className={classNames(
        styles.description,
        !isShowingMore && styles.descriptionMinimized
      )}
      dir="auto"
      ref={(el) => {
        if (el) setIsDescriptionOverflowing(el.offsetHeight < el.scrollHeight);
      }}
    >
      {isShowingMore ? description : firstLineOfDescription}
    </p>
  );

  const linksElement =
    linkControls.length > 0 ? (
      <section aria-labelledby={linksHeadingId} className={styles.subSection}>
        <h2 className={styles.subtitle} id={linksHeadingId}>
          {t('related')}
        </h2>
        <Controls controls={linkControls} as="link" direction="vertical" />
      </section>
    ) : null;

  const programInfoElement =
    programInfoLabels.length > 0 ? (
      <section
        aria-labelledby={programInfoHeadingId}
        className={styles.subSection}
      >
        <h2 className={styles.subtitle} id={programInfoHeadingId}>
          {t('programInfo')}
        </h2>
        <HeaderLabels
          cardKey={cardKey}
          separator={'lineBreak'}
          labels={programInfoLabels}
        />
      </section>
    ) : null;

  const hasShowMoreContent =
    isShowingMore ||
    !!linksElement ||
    !!programInfoElement ||
    hasMoreDescription;
  const contentClassName = classNames(
    styles.headerContent,
    areenaService === 'radio' && styles.headerContentPodcast
  );

  const isSquareImage = headerImageAspectRatio === '1:1';

  return (
    <section className={headerClassName}>
      <GridContainer>
        {image ? (
          <>
            <GridElement // Component to add spacing between a square-image and left edge/element
              mobile={isSquareImage ? 3 : 0}
              tablet={0}
              desktopSmall={isSquareImage ? 2 : 0}
              orderDesktopSmall={1}
            />
            <GridElement
              mobile={isSquareImage ? 6 : 12}
              tablet={isSquareImage ? 6 : 10}
              tabletLandscape={isSquareImage ? 6 : 9}
              desktopSmall={isSquareImage ? 6 : 8}
              desktop={isSquareImage ? 8 : 12}
              orderDesktopSmall={2}
            >
              <div className={styles.image}>
                <HeaderImage
                  aspectRatio={headerImageAspectRatio}
                  image={image}
                />
              </div>
            </GridElement>
          </>
        ) : null}
        <GridElement mobile={12} tablet={10} desktopSmall={16} desktop={12}>
          <div className={contentClassName}>
            {preTitleControls.length > 0 ? (
              <div className={styles.preTitleControls}>
                <Controls controls={preTitleControls} as="button" />
              </div>
            ) : null}
            {overlayLogoImage ? (
              <div className={styles.logo}>
                <LogoImage logoImage={overlayLogoImage} />
              </div>
            ) : null}
            {viewTitle && (
              <h1
                className={classNames(
                  styles.title,
                  isTitleLarge(headerCard) && styles.titleLarge
                )}
                dir="auto"
              >
                {viewTitle}
              </h1>
            )}
            {playControl ? (
              <div className={styles.playControls}>
                <HeaderPlayControls
                  playControl={playControl}
                  programMediaType={programMediaType}
                  programRegion={programRegion}
                  queueControl={queueControl}
                  viewUuid={viewUuid}
                />
              </div>
            ) : null}
            <HeaderLabels
              labels={labels.filter((l) => l.priority === 0)}
              separator="bullet"
              cardKey={cardKey}
            />

            {descriptionElement}

            <HeaderLabels
              labels={labels.filter((l) => l.priority === 1)}
              separator="line"
              cardKey={cardKey}
            />

            {hasShowMoreContent && (
              <ShowMore
                isShowingMore={isShowingMore}
                toggleShowMore={() =>
                  setIsShowingMore((prevState) => !prevState)
                }
              >
                {linksElement}
                {programInfoElement}
              </ShowMore>
            )}

            <Controls
              controls={actionControls}
              variation="text"
              as="button"
              padding="vertical"
            />
          </div>
        </GridElement>
      </GridContainer>
    </section>
  );
};
